import { useLanguage } from "@/contexts/LanguageContext";
import { Briefcase, Compass, Users, ShieldCheck, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TrainingSection = () => {
  const { t } = useLanguage();

  const programs = [
    {
      key: "training_program_1",
      color: "from-blue-600/20 via-slate-600/15 to-emerald-600/20 dark:from-blue-500/30 dark:via-slate-500/25 dark:to-emerald-500/30",
      icon: <Briefcase className="w-8 h-8 text-blue-600 dark:text-blue-400" />
    },
    {
      key: "training_program_2",
      color: "from-slate-600/20 via-blue-600/15 to-emerald-600/20 dark:from-slate-500/25 dark:via-blue-500/30 dark:to-emerald-500/30",
      icon: <Compass className="w-8 h-8 text-blue-600 dark:text-blue-400" />
    },
    {
      key: "training_program_3",
      color: "from-emerald-600/20 via-blue-600/15 to-slate-600/20 dark:from-emerald-500/30 dark:via-blue-500/25 dark:to-slate-500/30",
      icon: <Users className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
    },
    {
      key: "training_program_4",
      color: "from-blue-600/20 via-emerald-600/15 to-slate-600/20 dark:from-blue-500/30 dark:via-emerald-500/25 dark:to-slate-500/30",
      icon: <ShieldCheck className="w-8 h-8 text-slate-600 dark:text-slate-300" />
    }
  ];

  return (
    <section id="training" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute right-1/4 top-1/4 h-96 w-96 rounded-full bg-blue-500/8 blur-[120px] dark:bg-blue-600/10"></div>
        <div className="absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-emerald-500/8 blur-[120px] dark:bg-emerald-600/10"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <h2 className="section-heading mb-6">
            {t("training_section_title")}
          </h2>
          <p className="section-subtitle mx-auto max-w-3xl">
            {t("training_section_subtitle")}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {programs.map((program) => {
            const data = t(program.key) as any;
            return (
              <div
                key={program.key}
                className="soft-card group relative overflow-hidden"
              >
                <div className={cn("relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br p-8 dark:border-slate-800/70", program.color)}>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-slate-500/5 to-emerald-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="relative flex items-center justify-between gap-4">
                    <div className="flex items-center">
                      <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 shadow-sm backdrop-blur dark:bg-slate-950/60">
                        {program.icon}
                      </div>
                      <h3 className="text-2xl font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                        {data.title}
                      </h3>
                    </div>
                    <Badge className="border border-slate-200/80 bg-white/90 text-slate-700 shadow-sm dark:border-slate-800/70 dark:bg-slate-950/80 dark:text-slate-300">
                      {data.level}
                    </Badge>
                  </div>
                </div>

                <div className="p-8">
                  <div className="mb-6 flex flex-wrap gap-6">
                    <div className="flex items-center">
                      <Clock className="mr-2 h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        <strong className="mr-1 font-medium text-slate-900 dark:text-white">{t("training_duration")}:</strong> {data.duration}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Users className="mr-2 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        <strong className="mr-1 font-medium text-slate-900 dark:text-white">{t("training_format")}:</strong> {data.format}
                      </span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-slate-900 dark:text-white">{t("training_modules")}</h4>
                    <ul className="space-y-2">
                      {data.modules.map((module: string, idx: number) => (
                        <li key={idx} className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                          <span className="mr-3 h-2.5 w-2.5 rounded-full bg-blue-600 dark:bg-blue-400"></span>
                          {module}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-full border border-slate-200 bg-white/80 text-slate-800 transition-all duration-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-900"
                      onClick={() => {
                        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                        setTimeout(() => {
                          (window as any).toast && (window as any).toast({
                            title: data.title,
                            description: t("training_details") + ": " + data.title,
                          });
                        }, 700);
                      }}
                    >
                      {t("training_details")}
                    </Button>
                    <Button
                      size="lg"
                      className="rounded-full bg-blue-600 text-white shadow-[0_16px_45px_-20px_rgba(37,99,235,0.7)] transition-all duration-300 hover:bg-blue-700"
                      onClick={() => {
                        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                        setTimeout(() => {
                          (window as any).toast && (window as any).toast({
                            title: t("training_register"),
                            description: t("training_register") + ": " + data.title,
                          });
                        }, 700);
                      }}
                    >
                      {t("training_register")}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrainingSection;
